import { useEffect, useMemo } from 'react';
import { batch } from 'react-redux';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { isAnalysisLoadingSelector } from '../../../redux/selectors/loadingAnalyses';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  IAnalysisQuery,
  IAnalysisQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { GET_ANALYSIS } from '../../../utils/apollo/analysis';
import { loadAnalysisAction } from '../../../redux/slices/loadingAnalyses';
import { assertNotNil } from '../../../utils/types';
import {
  clearAnalysisAction,
  populateAnalysisThunk,
} from '../../../redux/slices/analyses';
import { analysisSelector } from '../../../redux/selectors/analyses';
import { Chart } from '../../../components/Chart';
import { ChartMode } from '../../../components/Chart/types';
import { ChartLoader } from '../../../components/Chart/ChartLoader';

interface IProps {
  analysisId: string;
  display: string;
}

const mode = ChartMode.Compact;

const StyledChart = styled(Chart)`
  // Chart should cover entire slide width and height
  height: 100%;
`;

const FavoritedReport = ({ analysisId, display }: IProps) => {
  const dispatch = useAppDispatch();

  const loadAnalysisInStore = ({ analysis }: IAnalysisQuery) => {
    assertNotNil(analysis);
    batch(() => {
      dispatch(
        populateAnalysisThunk({
          analysis,
        }),
      );
      dispatch(loadAnalysisAction(analysis.id));
    });
  };

  useQuery<IAnalysisQuery, IAnalysisQueryVariables>(GET_ANALYSIS, {
    variables: {
      analysisId,
      refresh: false,
    },
    onCompleted: loadAnalysisInStore,
  });

  const isAnalysisLoadingMemoSelector = useMemo(isAnalysisLoadingSelector, []);
  const isAnalysisLoading = useAppSelector((state) =>
    isAnalysisLoadingMemoSelector(state, analysisId),
  );
  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentAnalysis = useAppSelector((state) =>
    currentAnalysisMemoizedSelector(state, analysisId),
  );

  // clean-up analysis data on unmount
  useEffect(() => {
    return () => {
      dispatch(clearAnalysisAction(analysisId));
    };
  }, [analysisId, dispatch]);

  if (isAnalysisLoading || !currentAnalysis) {
    return <ChartLoader mode={mode} display={display} />;
  }

  return <StyledChart analysisId={analysisId} mode={mode} />;
};

export default FavoritedReport;
